import { useState, useEffect, useCallback } from 'react';
import { platformsApi } from '@/services/api';
import { platforms as staticPlatforms, calculateSuitability } from '@/data/platforms';

/**
 * Hook to fetch and manage platforms data
 * Falls back to static data if API fails
 */
export const usePlatforms = (enabledOnly = true) => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPlatforms = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await platformsApi.getAll(enabledOnly);
      if (data && data.length > 0) {
        setPlatforms(data);
      } else {
        // Fall back to static data if DB is empty
        const filteredStatic = enabledOnly 
          ? staticPlatforms.filter(p => p.is_enabled !== false)
          : staticPlatforms;
        setPlatforms(filteredStatic);
      }
    } catch (err) {
      console.error('Failed to fetch platforms from API:', err);
      setError(err);
      // Fall back to static data on error
      const filteredStatic = enabledOnly 
        ? staticPlatforms.filter(p => p.is_enabled !== false)
        : staticPlatforms;
      setPlatforms(filteredStatic);
    } finally {
      setLoading(false);
    }
  }, [enabledOnly]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  const getPlatformById = useCallback((id) => {
    return platforms.find(p => p.id === id);
  }, [platforms]);

  const refetch = useCallback(() => {
    return fetchPlatforms();
  }, [fetchPlatforms]);

  return {
    platforms,
    loading,
    error,
    getPlatformById,
    refetch,
    calculateSuitability,
  };
};

/**
 * Simpler hook to get a single platform by ID
 */
export const usePlatform = (platformId) => {
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!platformId) {
      setLoading(false);
      return;
    }

    const fetchPlatform = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await platformsApi.get(platformId);
        setPlatform(data);
      } catch (err) {
        console.error('Failed to fetch platform:', err);
        setError(err);
        // Fall back to static data
        const staticPlatform = staticPlatforms.find(p => p.id === platformId);
        setPlatform(staticPlatform || null);
      } finally {
        setLoading(false);
      }
    };

    fetchPlatform();
  }, [platformId]);

  return { platform, loading, error };
};

export default usePlatforms;
